/**
 * Centralized fetch utility for ts-core
 * Automatically includes Authorization header when user is logged in
 * and configured apiKey/partnerKey headers
 */

/**
 * Global configuration for apiFetch
 */
let globalConfig: {
  apiKey?: string;
  partnerKey?: string | null;
} = {};

/**
 * Configure global defaults for apiFetch
 * Call this once during app initialization
 */
export function configureApiFetch(config: {
  apiKey?: string;
  partnerKey?: string | null;
}): void {
  globalConfig = { ...globalConfig, ...config };
}

/**
 * Get the current apiFetch configuration
 */
export function getApiFetchConfig(): typeof globalConfig {
  return { ...globalConfig };
}

/**
 * Get the current auth token from localStorage
 */
function getAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('token');
}

export interface ApiFetchOptions extends RequestInit {
  apiKey?: string;
  partnerKey?: string | null;
  authToken?: string;
}

/**
 * Centralized fetch utility that automatically includes Authorization header
 * when user is logged in (token exists in localStorage).
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options including apiKey, partnerKey, and authToken
 * @returns Promise<Response>
 */
export async function apiFetch(
  url: string,
  options: ApiFetchOptions = {}
): Promise<Response> {
  const { apiKey, partnerKey, authToken, headers: customHeaders, ...fetchOptions } = options;
  
  // Get token from localStorage if not explicitly provided
  const token = authToken || getAuthToken();
  
  // Use global config as fallback for apiKey and partnerKey
  const effectiveApiKey = apiKey ?? globalConfig.apiKey;
  const effectivePartnerKey = partnerKey ?? globalConfig.partnerKey;
  
  // Check if body is FormData (file upload) - don't set Content-Type
  const isFormData = fetchOptions.body instanceof FormData;
  
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(customHeaders as Record<string, string> || {}),
  };

  // Remove undefined headers (used to override defaults)
  Object.keys(headers).forEach(key => {
    if (headers[key] === undefined) {
      delete headers[key];
    }
  });

  // Add API key if provided (from options or global config)
  if (effectiveApiKey) {
    headers['Api-Key'] = effectiveApiKey;
  }

  // Add partner key if provided (from options or global config)
  if (effectivePartnerKey) {
    headers['x-partner-key'] = effectivePartnerKey;
  }

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, {
    ...fetchOptions,
    headers,
  });
}
